import React, { useState, useContext, } from 'react';
import { useLocation } from 'react-router-dom';
import {Form, Button, Checkbox, Select, Input, Row, Col, Space, message } from 'antd';
import { Modal } from '../../UI/Modal/Modal.component';
import { isValidOracleID, validatePhoneNumber, maskPhoneNumber, isValidPhoneNumber, isValidEmail, isValidContractNumber, } from '../../../Validation';
import { createLocation } from '../../../services/StationService';
import { findUniqueStation } from '../../../services/MainService';
import { NocItemContext } from '../../../context/noc-item-context';
import { StationContext } from '../../../context/station-context';

const { Item } = Form;
const { Option } = Select;

const MAX_ORACLE_ID_LENGTH = 6;

export const arrStates = [
    {abbreviation: 'AL', name: 'Alabama'}, 
    {abbreviation: 'AK', name: 'Alaska'}, 
    {abbreviation: 'AZ', name: 'Arizona'},
    {abbreviation: 'AR', name: 'Arkansas'},
    {abbreviation: 'CA', name: 'California'},
    {abbreviation: 'CO', name: 'Colorado'},
    {abbreviation: 'CT', name: 'Connecticut'},
    {abbreviation: 'DE', name: 'Delaware'},
    {abbreviation: 'DC', name: 'District Of Columbia'},
    {abbreviation: 'FL', name: 'Florida'},
    {abbreviation: 'GA', name: 'Georgia'},
    {abbreviation: 'HI', name: 'Hawaii'},
    {abbreviation: 'ID', name: 'Idaho'},
    {abbreviation: 'IL', name: 'Illinois'},
    {abbreviation: 'IN', name: 'Indiana'},
    {abbreviation: 'IA', name: 'Iowa'},
    {abbreviation: 'KS', name: 'Kansas'},
    {abbreviation: 'KY', name: 'Kentucky'},
    {abbreviation: 'LA', name: 'Louisiana'},
    {abbreviation: 'ME', name: 'Maine'},
    {abbreviation: 'MD', name: 'Maryland'},
    {abbreviation: 'MA', name: 'Massachusetts'},
    {abbreviation: 'MI', name: 'Michigan'},
    {abbreviation: 'MN', name: 'Minnesota'},
    {abbreviation: 'MS', name: 'Mississippi'},
    {abbreviation: 'MO', name: 'Missouri'},
    {abbreviation: 'MT', name: 'Montana'},
    {abbreviation: 'NE', name: 'Nebraska'},
    {abbreviation: 'NV', name: 'Nevada'},
    {abbreviation: 'NH', name: 'New Hampshire'},
    {abbreviation: 'NJ', name: 'New Jersey'},
    {abbreviation: 'NM', name: 'New Mexico'},
    {abbreviation: 'NY', name: 'New York'},
    {abbreviation: 'NC', name: 'North Carolina'},
    {abbreviation: 'ND', name: 'North Dakota'},
    {abbreviation: 'OH', name: 'Ohio'},
    {abbreviation: 'OK', name: 'Oklahoma'},
    {abbreviation: 'OR', name: 'Oregon'},
    {abbreviation: 'PA', name: 'Pennsylvania'},
    {abbreviation: 'RI', name: 'Rhode Island'},
    {abbreviation: 'SC', name: 'South Carolina'},
    {abbreviation: 'SD', name: 'South Dakota'},
    {abbreviation: 'TN', name: 'Tennessee'},
    {abbreviation: 'TX', name: 'Texas'},
    {abbreviation: 'UT', name: 'Utah'},
    {abbreviation: 'VT', name: 'Vermont'},
    {abbreviation: 'VA', name: 'Virginia'},
    {abbreviation: 'WA', name: 'Washington'},
    {abbreviation: 'WV', name: 'West Virginia'},
    {abbreviation: 'WI', name: 'Wisconsin'},
    {abbreviation: 'WY', name: 'Wyoming'},
] 

export const AddNewStationFormModal = ({displayModal, setDisplayModal, setDisplayLastItem, refreshHandler, ...props}) => {

    const location = useLocation();
    const { currentNocItem } = location.state;

    const [nocItem, dispatchNocItem] = useContext(NocItemContext);
    const [stationState, stationDispatch] = useContext(StationContext);

    const [form] = Form.useForm();
    const [phoneAlerts, setPhoneAlerts] = useState(false);
    const [emailAlerts, setEmailAlerts] = useState(false);

    const onCancel = () => {
        form.resetFields();
        setDisplayModal(false);
    }

    const onFinish = (data) => {
        
        Object.assign(data, {username: currentNocItem.username, enableEmailAlerts: data.alerts.includes("enableEmailAlerts"), enablePhoneAlerts: data.alerts.includes("enablePhoneAlerts") });
        
        createLocation(data).then( function(response) {
            if( !response.result ) {
                message.error(response.message, 5);
                return;
            }
            
            message.success(`Station ${data.name} was successfully created.`, 5);
            
            dispatchNocItem({
                type: 'ADD_STATION', 
                payload: data,
            })

            refreshHandler();

            findUniqueStation({id: response.data?.id}).then( function ( response ){
                if( response ) {
                    stationDispatch({
                        type: "SET_STATION",
                        payload: response,
                    });
                }
            })

            setDisplayLastItem(true);
            setDisplayModal(false);
        });
    }

    return (
        <>
            <Modal 
                title='Add Station' 
                visible={displayModal} 
                setVisible={setDisplayModal} 
                width={500} 
                actionGroup={false}
            >
                <Form
                    form={form}
                    layout='vertical'
                    autoComplete='off'
                    name='AddNewStationForm'
                    onFinish={(values) => onFinish(values)}
                    initialValues={{
                        station: '', 
                        oracleID: '', 
                        contractNumber: '', 
                        emailAddress: '', 
                        phoneNumber: '', 
                        phoneCarrier: '', 
                        alerts: [], 
                        comments: '',
                        addressStreet: '',
                        addressCity: '', 
                        addressState: '', 
                        addressZipCode: '', 
                    }}
                >
                    <Item label='Station/Site Name:' name='name' hasFeedback
                        rules={[
                            {
                                required: true, 
                                message: 'Station name is required.', 
                            },
                            {
                                validator: (rule, value) => {

                                    const hasLeadingWhitespace = (str) => {
                                        return !(/^\S/.test(str));
                                    }

                                    if(!rule.required && !value){
                                            return Promise.resolve();
                                        }

                                    if(value){
                                        if(hasLeadingWhitespace(value)){
                                            return Promise.reject(new Error('Station name cannot start with whitespace.'));
                                        }
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Please enter a valid station name'))
                                }
                            }
                        ]}>
                        <Input/>
                    </Item>
                    <Item label='Oracle ID:' name='oracleID' tooltip='Oracle ID numbers are 5-6 digits' hasFeedback className='no-spinner'
                            rules={[
                                {
                                    required: false, 
                                    message: 'Oracle ID Numbers must be 5-6 digits.',
                                    validator: (rule, value) => {
                                        if(!rule.required && !value){
                                            return Promise.resolve();
                                        }

                                        if (value && isValidOracleID(value)) {
                                            return Promise.resolve();
                                            }
                                        return Promise.reject(new Error('Oracle ID must be 5-6 digits.'));
                                    }
                                }, 
                            ]}>
                        <Input type='number' className='no-spinner' min='0' onInput={e => e.target.value = e.target.value.slice(0,MAX_ORACLE_ID_LENGTH)} />
                    </Item>
                    <Item label='Contract Number:' name='contractNumber' hasFeedback tooltip='Maximum 30 alphanumeric characters'
                        rules={[
                            {
                                required: false, 
                                message: 'Please enter maximum 30 alphanumeric characters.',
                                validator: (rule, value) => {
                                    if(!rule.required && !value){
                                            return Promise.resolve();
                                        }

                                    if (value && isValidContractNumber(value)) {
                                        return Promise.resolve();
                                        }
                                    return Promise.reject(new Error('Please enter maximum 30 alphanumeric characters.'));
                                }
                            }
                        ]}
                    >
                        <Input type='text' showCount />
                    </Item>
                    <Item 
                        label='Email Address:' 
                        name='email' 
                        hasFeedback
                        rules={[
                                {
                                    required: emailAlerts,
                                    message: 'Email address is required.',
                                },
                                {
                                    type: 'email',
                                    validator: (rule, value) => {
                                        
                                        if(!rule.required && !value){
                                            return Promise.resolve();
                                        }

                                        if (value && isValidEmail(value)) {
                                            return Promise.resolve();
                                        }
                                        
                                        return Promise.reject(new Error('Enter valid email address.'));
                                    }
                                },
                            ]}
                        >
                        <Input type='email'/>
                    </Item>
                    <Item label=" Street Address:" name="streetAddress">
                        <Input/>
                    </Item>
                    <Item>
                        <Row gutter={12}>
                            <Col flex={1}>
                                <Item label='City:' name='city'>
                                    <Input/>
                                </Item>
                            </Col>
                            <Col span={6}>
                                <Item label='State:' name='state'>
                                    <Select placeholder='Select State' >
                                    {
                                        arrStates.map((item) => {
                                            return <Option key={item.abbreviation} value={item.abbreviation}>{item.name}</Option>
                                        })
                                    }
                                    </Select>
                                </Item>
                            </Col>
                            <Col span={6}>
                                <Item label='Zip Code:' name='zip'>
                                    <Input type='number' className='no-spinner' onInput={e => e.target.value = e.target.value.slice(0,5)}/>
                                </Item>
                            </Col>
                        </Row>
                    </Item>
                    <Item>
                        <Row gutter={12}>
                            <Col flex="auto">
                                <Item 
                                    label='Phone Number:' 
                                    name='phone' 
                                    hasFeedback
                                    validateTrigger='onChange'        
                                    rules={[
                                        {
                                            required: phoneAlerts,
                                            message: 'Phone number is required.',
                                        },
                                        {
                                            validator: (rule, value) => {
                                                
                                                if(!rule.required && !value){
                                                    return Promise.resolve();
                                                }

                                                if (value && validatePhoneNumber(value) && isValidPhoneNumber(value)) {
                                                    return Promise.resolve();
                                                }
                                                
                                                return Promise.reject(new Error('Enter valid phone number.'));
                                                }
                                            },
                                        ]}     
                                    >
                                    <Input className='no-spinner' onInput={e => maskPhoneNumber(e)}/>
                                </Item>
                            </Col>
                            <Col>
                                <Item label='Phone Carrier:' name='phoneCarrier' rules={[{required: phoneAlerts, message: 'Please select a phone carrier.',}]}>
                                    <Select placeholder="Please choose one" style={{width: '100%'}} >
                                        <Option value="AT&T">AT&T</Option>
                                        <Option value="Google-Fi">Google-Fi</Option>
                                        <Option value="Sprint">Sprint</Option>
                                        <Option value="T-Mobile">T-Mobile</Option>
                                        <Option value="Verizon">Verizon</Option>
                                    </Select>
                                </Item>
                            </Col>
                        </Row>
                    </Item>
                    <Item label='Alert Preferences:' name='alerts' tooltip="Check box to enable alerts">
                        <Checkbox.Group>
                            <Checkbox value='enablePhoneAlerts' checked={phoneAlerts} onChange={(e) => setPhoneAlerts(e.target.checked)}>
                                Phone Alerts
                            </Checkbox>
                            <Checkbox value='enableEmailAlerts' checked={emailAlerts} onChange={(e) => setEmailAlerts(e.target.checked)}>
                                Email Alerts
                            </Checkbox>
                        </Checkbox.Group>
                    </Item>
                    <Item label='Comments:' name='comment'>
                        <Input.TextArea placeholder='Optional' autoSize={{ minRows: 3, maxRows: 5 }}/>
                    </Item>
                    <Row justify='end'>
                        <Space>
                            <Button onClick={onCancel}>Cancel</Button>
                            <Button type='primary' htmlType='submit'>Add Station</Button>
                        </Space>
                    </Row>
                </Form>
            </Modal>
        </>
    );
}